// ============================================================================
// TEMPLATE PREVIEW PAGE
// View template details with phase breakdown
// ============================================================================

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, AlertCircle, ChevronRight } from 'lucide-react';
import { SessionTemplate, TemplateSnapshot } from '../types';
import { previewSessionTemplate } from '../services/templateService';
import TemplatePreview from '../components/template/TemplatePreview';

export default function TemplatePreviewPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [template, setTemplate] = useState<SessionTemplate | null>(null);
  const [phaseTemplates, setPhaseTemplates] = useState<TemplateSnapshot['phases']>([]);

  const loadTemplatePreview = useCallback(async () => {
    if (!templateId) {
      setError('Template ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await previewSessionTemplate(templateId);
      setTemplate(data.template);
      setPhaseTemplates(data.snapshot.phases);
    } catch (err: any) {
      console.error('Failed to load template preview:', err);
      setError(err.message || 'Failed to load template preview');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadTemplatePreview();
  }, [loadTemplatePreview]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-text-sub">Loading template preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center px-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-text-main mb-2">
            Error Loading Template
          </h2>
          <p className="text-sm text-text-sub mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/templates')}
            className="px-6 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium shadow-sm transition-colors text-sm"
          >
            Back to Templates
          </button>
        </div>
      </div>
    );
  }

  if (!template) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background-light">
      <div className="w-full max-w-[900px] mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-text-sub mb-6">
          <button
            onClick={() => navigate('/templates')}
            className="hover:text-primary transition-colors"
          >
            Templates
          </button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-text-main font-medium">Preview</span>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-text-main mb-2">
              {template.name}
            </h1>
            <p className="text-text-sub">
              Preview template configuration and phase structure
            </p>
          </div>
          <button
            onClick={() => navigate(`/templates/${templateId}/edit`)}
            className="px-5 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium shadow-sm transition-colors text-sm flex items-center gap-2 flex-shrink-0"
          >
            <Edit className="w-4 h-4" />
            Edit Template
          </button>
        </div>

        {/* Preview Component */}
        <TemplatePreview template={template} phaseTemplates={phaseTemplates} />

        {/* Bottom Back Button */}
        <div className="mt-6">
          <button
            onClick={() => navigate('/templates')}
            className="px-5 py-2.5 rounded-lg border border-slate-300 text-text-main hover:bg-slate-50 font-medium transition-colors text-sm flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Templates
          </button>
        </div>
      </div>
    </div>
  );
}
